import axios from "axios";
import React, { useState } from "react";

import { api } from "../lib/api";
import { userFormSchema } from "../schemas/settings";
import { UserMini } from "../types/crm";

interface FormState {
  mode: "create" | "edit";
  user?: UserMini;
}

interface UsersSectionProps {
  users: UserMini[];
  onUsersChange: (users: UserMini[]) => void;
}

export function UsersSection({ users, onUsersChange }: UsersSectionProps) {
  const [form, setForm] = useState<FormState | null>(null);
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openCreate = () => {
    setUsername("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setIsActive(true);
    setFieldErrors({});
    setFormError(null);
    setForm({ mode: "create" });
  };

  const openEdit = (user: UserMini) => {
    setUsername(user.username);
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setEmail(user.email);
    setPassword("");
    setIsActive(user.is_active);
    setFieldErrors({});
    setFormError(null);
    setForm({ mode: "edit", user });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const result = userFormSchema.safeParse({
      username,
      first_name: firstName,
      last_name: lastName,
      email,
    });

    const errors: Record<string, string> = {};
    if (!result.success) {
      const flat = result.error.flatten().fieldErrors;
      for (const key of Object.keys(flat)) {
        errors[key] = flat[key as keyof typeof flat]?.[0] ?? "Campo inválido";
      }
    }
    if (form?.mode === "create" && !password) {
      errors.password = "Informe a senha";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    try {
      const payload = {
        ...result.data!,
        is_active: isActive,
        ...(password ? { password } : {}),
      };

      if (form?.mode === "edit" && form.user) {
        const response = await api.patch<UserMini>(`/users/${form.user.id}/`, payload);
        onUsersChange(users.map((user) => (user.id === response.data.id ? response.data : user)));
      } else {
        const response = await api.post<UserMini>("/users/", payload);
        onUsersChange([...users, response.data]);
      }
      setForm(null);
    } catch {
      setFormError("Não foi possível salvar o usuário.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (user: UserMini) => {
    if (!window.confirm(`Excluir o usuário "${user.username}"?`)) return;
    try {
      await api.delete(`/users/${user.id}/`);
      onUsersChange(users.filter((existing) => existing.id !== user.id));
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      window.alert(detail ?? "Não foi possível excluir o usuário.");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Responsáveis</h2>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
        >
          Novo responsável
        </button>
      </div>

      <div className="mt-3 overflow-x-auto rounded-[10px] border border-border-subtle">
        <table className="min-w-full divide-y divide-border-subtle text-sm">
          <thead className="bg-surface">
            <tr>
              {["Usuário", "Nome", "Email", "Ativo", ""].map((header) => (
                <th key={header} className="px-4 py-2 text-left font-medium text-slate-600">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-4 py-2 text-slate-800">{user.username}</td>
                <td className="px-4 py-2 text-slate-600">
                  {[user.first_name, user.last_name].filter(Boolean).join(" ")}
                </td>
                <td className="px-4 py-2 text-slate-600">{user.email}</td>
                <td className="px-4 py-2 text-slate-600">{user.is_active ? "Sim" : "Não"}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(user)}
                    className="mr-3 text-sm font-medium text-primary hover:underline"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(user)}
                    className="text-sm font-medium text-red-600 hover:underline"
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/30 py-8">
          <div className="w-full max-w-sm rounded-[10px] border border-border-subtle bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800">
              {form.mode === "edit" ? "Editar responsável" : "Novo responsável"}
            </h3>
            <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-slate-700">
                  Usuário
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {fieldErrors.username && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.username}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-slate-700">
                    Nome
                  </label>
                  <input
                    id="first_name"
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-slate-700">
                    Sobrenome
                  </label>
                  <input
                    id="last_name"
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  {form.mode === "edit" ? "Nova senha" : "Senha"}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {form.mode === "edit" && (
                  <p className="mt-1 text-xs text-slate-500">Deixe em branco para manter a atual.</p>
                )}
                {fieldErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setIsActive(event.target.checked)}
                />
                Ativo
              </label>

              {formError && <p className="text-sm text-red-600">{formError}</p>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60"
                >
                  {isSubmitting ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
